"use client"

import { useState, useRef, useCallback, useEffect } from 'react'

export interface UseIdleTimerParams {
  /** 총 타임아웃 시간 (밀리초) - 기본 30초 */
  idleTimeoutMs?: number
  /** 경고 표시 시작 시점 (남은 밀리초) - 기본 10초 */
  warningMs?: number
  /** 타이머 활성화 조건 */
  enabled: boolean
  /** 일시정지 조건 (speaking/loading 등) */
  isPaused: boolean
  /** 타임아웃 시 콜백 */
  onTimeout: () => void
  /** 디버그 로그 접두사 */
  debugPrefix?: string
}

export interface UseIdleTimerReturn {
  /** 타이머 경고 UI 표시 여부 */
  isTimerVisible: boolean
  /** 남은 초 */
  remainingSeconds: number
  /** 타이머 시작 (lastActivity 리셋) */
  startTimer: () => void
  /** 타이머 정지 (다음 startTimer까지 비활성) */
  stopTimer: () => void
  /** 타이머 재시작 (startTimer와 동일) */
  resetTimer: () => void
  /** 타이머 활성 상태 */
  isActive: boolean
}

/**
 * 유휴 타이머 공통 Hook
 *
 * 일정 시간 동안 활동이 없으면 onTimeout 콜백을 호출합니다.
 * 키오스크, 웹 등 다양한 모드에서 재사용 가능합니다.
 *
 * @example
 * ```tsx
 * const idleTimer = useIdleTimer({
 *   enabled: hasConsented,
 *   isPaused: indicatorState === 'speaking',
 *   onTimeout: () => resetSession(),
 *   debugPrefix: 'KIOSK',
 * })
 *
 * // 세션 시작 시
 * idleTimer.startTimer()
 *
 * // 사용자 활동 감지 시
 * idleTimer.stopTimer()
 * ```
 */
export const useIdleTimer = ({
  idleTimeoutMs = 30000,
  warningMs = 10000,
  enabled,
  isPaused,
  onTimeout,
  debugPrefix = 'IDLE',
}: UseIdleTimerParams): UseIdleTimerReturn => {
  // ==================== 상태 ====================
  const [isTimerVisible, setIsTimerVisible] = useState(false)
  const [remainingSeconds, setRemainingSeconds] = useState(warningMs / 1000)

  // ==================== Refs ====================
  const isTimerActiveRef = useRef(false)
  const lastActivityRef = useRef<number>(Date.now())
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const isPausedRef = useRef(isPaused)
  const onTimeoutRef = useRef(onTimeout)

  // Refs 동기화 (stale closure 방지)
  isPausedRef.current = isPaused
  onTimeoutRef.current = onTimeout

  // ==================== 타이머 제어 함수 ====================
  const startTimer = useCallback(() => {
    isTimerActiveRef.current = true
    lastActivityRef.current = Date.now()
    setIsTimerVisible(false)
    setRemainingSeconds(warningMs / 1000)
    console.log(`⏱️ [${debugPrefix}] 타이머 시작`)
  }, [warningMs, debugPrefix])

  const stopTimer = useCallback(() => {
    isTimerActiveRef.current = false
    setIsTimerVisible(false)
    setRemainingSeconds(warningMs / 1000)
    console.log(`⏹️ [${debugPrefix}] 타이머 정지`)
  }, [warningMs, debugPrefix])

  const resetTimer = useCallback(() => {
    startTimer()
  }, [startTimer])

  // ==================== 타이머 인터벌 ====================
  useEffect(() => {
    // 비활성화 상태면 인터벌 정리
    if (!enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      return
    }

    // 1초마다 체크
    intervalRef.current = setInterval(() => {
      // 타이머 비활성 상태면 스킵
      if (!isTimerActiveRef.current) return

      // 일시정지 상태면 lastActivity 갱신 (타이머 유지)
      if (isPausedRef.current) {
        lastActivityRef.current = Date.now()
        // 🔧 FIX: 이미 false면 setState 호출 안 함 (불필요한 리렌더링 방지)
        // setIsTimerVisible(false) - 제거: 깜빡임 원인
        console.log(`⏸️ [${debugPrefix}] 일시정지 중 - 타이머 리셋`)
        return
      }

      const elapsed = Date.now() - lastActivityRef.current
      const remaining = Math.max(0, idleTimeoutMs - elapsed)
      const remainingSec = Math.ceil(remaining / 1000)

      // 🔧 DEBUG: 타이머 진행 상황 로깅
      console.log(`🕐 [${debugPrefix}] elapsed=${elapsed}ms, remaining=${remaining}ms, sec=${remainingSec}`)

      // 경고 표시 시작
      if (remaining <= warningMs && remaining > 0) {
        setIsTimerVisible(true)
        setRemainingSeconds(remainingSec)
      }

      // 타임아웃 도달 - 자동 정지 후 콜백 호출
      if (remaining <= 0) {
        console.log(`⏰ [${debugPrefix}] 유휴 타임아웃 - 자동 정지`)
        isTimerActiveRef.current = false
        setIsTimerVisible(false)
        onTimeoutRef.current()
      }
    }, 1000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [enabled, idleTimeoutMs, warningMs, debugPrefix])

  return {
    isTimerVisible,
    remainingSeconds,
    startTimer,
    stopTimer,
    resetTimer,
    isActive: isTimerActiveRef.current,
  }
}
